import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useUpdateAtom } from 'jotai/utils';
import { chatSessionOriginAtom, pendingChipAtom } from 'src/shared/chat';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';
import { useChatSuggestionsQuery, useLibraryCountQuery } from 'src/api/queries/chatQueries';
import { useAssetDetailsQuery } from 'src/api/queries/assetsQueries';
import { useIsMobile } from 'src/hooks/useIsMobile';
import useFileSpinPicker from 'src/hooks/useFileSpinPicker';
import { pickStarters } from '../nudges/starterGates';
import { useStarterGates } from '../nudges/useStarterGates';
import { useChatContext } from '../useChatContext';
import { ICON_MAP } from '../icons';
import type { CategoryPrompt, Starter, SuggestionsQuery } from '../types';
import { ExternalAgentPanelLink } from 'src/components/ExternalAgentMenu';
import { BrowseAllPanel } from './BrowseAllPanel';
import messages from './messages';
import styles from './ChatEmptyState.module.scss';

const STARTER_LIMIT = { desktop: 5, mobile: 3 } as const;

/** Pages with starters of their own (server slots chat.page.<page>) say what
 *  the agent will work with instead of the library count. */
const PAGE_CONTEXT: Record<string, typeof messages[keyof typeof messages]> = {
  collections: messages.contextCollections,
  'collection-detail': messages.contextCollection,
  shares: messages.contextShares,
  'share-detail': messages.contextShare,
};

/** Same rule as the server's AssetKind: image/video only when every type is. */
export function assetKind(types: string[]): 'image' | 'video' | 'mixed' {
  if (types.length === 0) return 'mixed';
  if (types.every((t) => t.startsWith('image/') || t === 'image')) return 'image';
  if (types.every((t) => t.startsWith('video/') || t === 'video')) return 'video';
  return 'mixed';
}

/**
 * The chat panel before the first message: a greeting, a line saying what the
 * agent will work with, up to five starter chips for that context, and
 * "Browse all" for the whole catalogue (ui#979).
 *
 * Starters come from GET /api/v1/chat/suggestions (catalogue:
 * filespin-mcp-server internal/prompts/starters.json). A chip SENDS its
 * sentence straight away — the user is already in the chat, so a second
 * click on Send adds nothing. "Browse all" items only prefill: they are
 * generic and usually want a detail added.
 */
export const ChatEmptyState: React.FC = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const history = useHistory();
  const isMobile = useIsMobile();
  const chatContext = useChatContext();
  const { hydrated, ctx: gateCtx } = useStarterGates();
  const setPendingChip = useUpdateAtom(pendingChipAtom);
  const setSelectedAssets = useUpdateAtom(selectedAssetsAtom);
  const setSessionOrigin = useUpdateAtom(chatSessionOriginAtom);
  const { uploadContent } = useFileSpinPicker();
  const [browsing, setBrowsing] = useState(false);

  const assetId =
    chatContext.currentPage === 'asset-detail' ? chatContext.currentAssetId : undefined;
  const { data: asset } = useAssetDetailsQuery(assetId ?? '', undefined, { enabled: !!assetId });

  const selected = chatContext.selectedAssetIds.length;
  const { data: libraryCount, isLoading: countLoading } = useLibraryCountQuery(!assetId);
  const libraryEmpty = !assetId && selected === 0 && libraryCount === 0;

  const query: SuggestionsQuery = assetId
    ? {
        surface: 'chat',
        page: 'asset-detail',
        selected: 1,
        assetTypes: [(asset as any)?.content_type ?? ''],
      }
    : {
        surface: 'chat',
        page: chatContext.currentPage,
        selected,
        assetTypes: selected > 0 ? chatContext.selectedAssetTypes : undefined,
        libraryEmpty,
      };

  // Hold the request until the facts that pick the slot are in, so a new
  // account does not flash the "nothing selected" chips before the
  // empty-library ones.
  const ready = assetId ? Boolean(asset) : selected > 0 || !countLoading;
  const { data } = useChatSuggestionsQuery(query, { enabled: ready });

  const starters = useMemo(
    () =>
      hydrated
        ? pickStarters(data?.starters, gateCtx, isMobile ? STARTER_LIMIT.mobile : STARTER_LIMIT.desktop)
        : [],
    [hydrated, data, gateCtx, isMobile],
  );

  const contextLine = useMemo(() => {
    if (assetId) {
      const name = (asset as any)?.name;
      return name
        ? formatMessage(messages.contextAsset, { name })
        : formatMessage(messages.contextAssetUnnamed);
    }
    if (selected > 0) {
      const kind = assetKind(chatContext.selectedAssetTypes);
      const msg =
        kind === 'image'
          ? messages.contextSelectionImage
          : kind === 'video'
          ? messages.contextSelectionVideo
          : messages.contextSelection;
      return formatMessage(msg, { count: selected });
    }
    if (libraryCount === 0) return formatMessage(messages.contextLibraryEmpty);
    const pageLine = PAGE_CONTEXT[chatContext.currentPage];
    if (pageLine) return formatMessage(pageLine);
    if (libraryCount === undefined) return '';
    return formatMessage(messages.contextLibrary, { count: libraryCount });
  }, [
    assetId,
    asset,
    selected,
    chatContext.selectedAssetTypes,
    chatContext.currentPage,
    libraryCount,
    formatMessage,
  ]);

  // On the asset page the asset is in view but not ticked; the starters say
  // "the selected asset", so point the agent's selection at it (the
  // asset-detail nudges do the same).
  const claimAsset = useCallback(() => {
    if (assetId) setSelectedAssets([assetId]);
  }, [assetId, setSelectedAssets]);

  const runStarter = (starter: Starter) => {
    if (starter.action === 'open_upload') {
      if (!history.location.pathname.startsWith('/assets')) history.push('/assets');
      uploadContent();
      return;
    }
    if (!starter.message) return;
    claimAsset();
    setSessionOrigin(`starter:${starter.id}`);
    setPendingChip({ label: starter.label, message: starter.message, sendNow: true });
  };

  const pickFromBrowse = (prompt: CategoryPrompt) => {
    claimAsset();
    // Fill asset_id when exactly one asset is in context; anything else the
    // prompt needs, the agent asks for (the proxy marks missing arguments).
    const oneAsset =
      assetId ?? (chatContext.selectedAssetIds.length === 1 ? chatContext.selectedAssetIds[0] : undefined);
    const mcpArgs =
      oneAsset && prompt.required_arguments?.includes('asset_id') ? { asset_id: oneAsset } : undefined;
    setPendingChip({
      label: prompt.description,
      message: prompt.description,
      mcpPrompt: prompt.name,
      mcpArgs,
    });
    setBrowsing(false);
  };

  if (browsing && data?.categories?.length) {
    return (
      <div className={styles.container}>
        <BrowseAllPanel
          categories={data.categories}
          onBack={() => setBrowsing(false)}
          onPick={pickFromBrowse}
        />
      </div>
    );
  }

  return (
    <div className={styles.container} data-test="chat-empty-state">
      <h2 className={styles.greeting}>{formatMessage(messages.greeting)}</h2>
      <p className={styles.context} data-test="chat-empty-context">
        {contextLine || ' '}
      </p>

      {starters.length > 0 && (
        <ul className={styles.starters} aria-label={formatMessage(messages.startersLabel)}>
          {starters.map((starter) => (
            <li key={starter.id}>
              <button
                type="button"
                className={styles.starter}
                onClick={() => runStarter(starter)}
                title={starter.message}
                data-test={`chat-starter-${starter.id}`}
              >
                {ICON_MAP[starter.icon] && (
                  <span className={styles.starterIcon} aria-hidden>
                    {ICON_MAP[starter.icon]}
                  </span>
                )}
                <span>{starter.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {Boolean(data?.categories?.length) && (
        <button
          type="button"
          className={styles.browseLink}
          onClick={() => setBrowsing(true)}
          data-test="chat-browse-all-link"
        >
          {formatMessage(messages.browseAll)}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <ExternalAgentPanelLink />
    </div>
  );
};

export default ChatEmptyState;
